import { Button, CircularProgress } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';
import { useCallback, useMemo, useState, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { ReactComponent as GrayAddSvg } from 'assets/svg/grayPlus.svg';
import { GENERATE_SIGNED_TAFFI_URL } from 'graph_ql/storage/generate_signed_url.gql';
import { GenerateSignedTaffiUrl, GenerateSignedTaffiUrlVariables } from '__generated__/GenerateSignedTaffiUrl';
import { ValidURL } from 'utils/utils';
import StandardPopup from 'component/StandardPopup';
import { ToastType } from '../../../constants';
import { useHistory } from 'react-router-dom';

interface IModalAddImage {
    currentList: string[];
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    handleSubmit: (images: string[]) => void;
    selectedIndex: number;
}

const useStyles = makeStyles(() => ({
    createContainer: {
        justifyContent: 'space-between',
        display: 'flex',
        alignItems: 'center',
        minWidth: 300,
        '& button': {
            height: 35,
            width: 50,
            marginTop: 25,
        },
    },
    content: {
        width: '70%',
        fontFamily: 'PoppinsRegular',
        fontWeight: 400,
        fontSize: '14px',
    },
    button: {
        // width: "80px!important",
        padding: '6px 16px!important',
        background: '#fff',
        width: '250px',
        height: '40px',
        lineHeight: '20px',
        fontFamily: 'PoppinsRegular',
        fontWeight: 400,
        borderRadius: '10px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        boxShadow: 'none!important',
        border: '1px solid #4A6C72',
        textTransform: 'capitalize',
        color: '#1D474F',
    },
    inputIcon: {
        position: 'absolute',
        right: '10px',
        width: '5px',
        height: '5px',
        top: '7px',
        color: '#1D474F',
    },
    input: {
        color: '#1D474F !important',
        border: 'none!important',
        width: '90%',
        textOverflow: 'ellipsis',
        '&::placeholder ': {
            fontFamily: 'PoppinsRegular',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            color: '#1D474F!important',
        },
    },
    btn: {
        padding: '13px 24px',
        borderRadius: '29px',
        fontFamily: 'PoppinsRegular',
        fontWeight: 600,
        minWidth: '90px',
    },
    activeBtn: {
        background: '#1D474F',
        color: '#fff',
    },
    loadingIcon: {
        marginRight: '10px',
    },
    errorMessage: {
        color: 'red!important',
        fontFamily: 'PoppinsRegular',
        fontSize: '14px',
        padding: 0,
        margin: 0,
    },
}));

// const Transition = React.forwardRef(function Transition(props, ref) {
//   return <Slide direction="up" ref={ref} {...props} />;
// });

const ModalAddImage = ({ isOpen, setIsOpen, handleSubmit, currentList, selectedIndex }: IModalAddImage) => {
    const classes = useStyles();
    const inputFileRef = useRef<HTMLInputElement | null>(null);
    const [listFiles, setListFiles] = useState<string[]>([]);
    const [fileUrl, setFileUrl] = useState<string>('');
    const [invalidFileUrl, setInvalidFileUrl] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const isButtonSaveEnable = useMemo<boolean>(() => ValidURL(fileUrl) || !!listFiles.length, [fileUrl, listFiles]);
    const [generateSignedUrlCb] = useMutation<GenerateSignedTaffiUrl, GenerateSignedTaffiUrlVariables>(
        GENERATE_SIGNED_TAFFI_URL,
    );
    const [modalShow, setModalShow] = useState({
        show: false,
        title: '',
        content: '',
        button1: '',
        button2: '',
        path: '',
        type: '',
        closeButton: true,
    });
    const history = useHistory();

    const generateFileUrl = useCallback(
        async (selectedFile: File) => {
            const signedUrl: any = await generateSignedUrlCb({
                variables: {
                    input: {
                        filename: uuidv4(),
                        mimetype: selectedFile.type,
                        folder: 'public/items',
                    },
                },
            });
            await axios.put(signedUrl.data?.generateSignedTaffiUrl?.signedUrl, selectedFile, {
                headers: {
                    'Content-Type': selectedFile.type,
                    'Cache-Control': 'no-cache',
                    'x-goog-acl': 'public-read',
                },
            });
            return signedUrl.data?.generateSignedTaffiUrl?.url || '';
        },
        [generateSignedUrlCb],
    );

    const handleChangeListImage = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files) {
                const files: FileList = e.target.files;

                const files_array: File[] = Object.values(files);
                setIsLoading(true);
                Promise.all(files_array.map((selectedFile) => generateFileUrl(selectedFile)))
                    .then((urls) => {
                        let listSingleImage = [...currentList];
                        if (urls.length > 0) {
                            listSingleImage[selectedIndex] = urls[0];
                        }
                        setListFiles(listSingleImage);
                    })
                    .catch((e) => {
                        console.error(e);
                        setModalShow({
                            show: true,
                            title: 'Something went wrong.',
                            content:
                                'An error occurred while processing the image. Please try again or select a different image.',
                            button1: 'Okay',
                            button2: '',
                            path: '',
                            type: ToastType.Error,
                            closeButton: true,
                        });
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedIndex],
    );

    const handleClearForm = useCallback(() => {
        setListFiles([]);
        setFileUrl('');
        setInvalidFileUrl(false);
    }, []);

    const handleSubmitListImage = (newList) => {
        handleSubmit(newList);
        handleClearForm();
        setIsOpen(false);
    };

    const testImage = (url, timeout = 5000) => {
        return new Promise(function (resolve, reject) {
            let timer,
                img = new Image();
            img.onerror = img.onabort = function () {
                clearTimeout(timer);
                reject('error');
            };
            img.onload = function () {
                clearTimeout(timer);
                resolve('success');
            };
            timer = setTimeout(function () {
                img.src = '//!!!!/test.jpg';
                reject('timeout');
            }, timeout);
            img.src = url;
        });
    };

    const handleSaveBtn = useCallback(() => {
        let newList = listFiles || [];
        //  ? {...listFiles} : null;
        if (fileUrl) {
            setIsLoading(true);
            testImage(fileUrl)
                .then(() => {
                    let uploadList = [...currentList];
                    uploadList[selectedIndex] = fileUrl;
                    handleSubmitListImage(uploadList);
                })
                .catch((e) => {
                    console.error(e);
                    setInvalidFileUrl(true);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            handleSubmitListImage(newList);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listFiles, fileUrl, currentList, selectedIndex]);

    return (
        <Dialog
            open={isOpen}
            //   TransitionComponent={Transition}
            keepMounted
            //   onClose={handleClose}
            aria-describedby="alert-dialog-slide-description"
        >
            <Grid container className={classes.createContainer}>
                <Grid item lg={11} md={11} sm={11} xs={11}>
                    <DialogTitle
                        style={{
                            textAlign: 'center',
                            color: '#1D474F',
                            fontSize: '18px',
                            marginLeft: '20px',
                        }}
                    >
                        {'Add image'}
                    </DialogTitle>
                </Grid>
                <Grid item lg={1} md={1} sm={1} xs={1} style={{ display: 'flex', flexDirection: 'row-reverse' }}>
                    <CloseIcon
                        onClick={() => {
                            setIsOpen(false);
                        }}
                        style={{ cursor: 'pointer', marginRight: '20px' }}
                    />
                </Grid>
            </Grid>

            <DialogContent
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    textAlign: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                }}
            >
                <p className={classes.content}>Add an image from photo gallery or enter an image URL</p>
                <Button
                    variant="contained"
                    component="label"
                    disabled={isLoading}
                    className={classes.button}
                    style={{
                        borderRadius: '42px',
                        borderColor: '#1D474F',
                        padding: '12px 30px',
                        opacity: !!fileUrl || isLoading ? '0.5' : '1',
                    }}
                    onClick={() => {
                        inputFileRef?.current?.focus();
                    }}
                >
                    {isLoading && !fileUrl ? (
                        <CircularProgress size={15} className={classes.loadingIcon} />
                    ) : (
                        <GrayAddSvg
                            style={{
                                marginRight: '10px',
                                height: '8px',
                                width: '8px',
                            }}
                        />
                    )}
                    Add from gallery
                    <input
                        type="file"
                        hidden
                        // multiple
                        accept="image/*"
                        ref={inputFileRef}
                        onChange={handleChangeListImage}
                        disabled={!!fileUrl || isLoading}
                    />
                </Button>

                <div
                    style={{
                        position: 'relative',
                        borderBottom: '1px solid #1D474F',
                        marginTop: '32px',
                        marginBottom: '36px',
                        height: '1px',
                        width: '100%',
                    }}
                >
                    <p
                        style={{
                            position: 'absolute',
                            left: 'calc(50% - 13px)',
                            top: '-13px',
                            background: '#fff',
                            marginBottom: '1rem',
                        }}
                    >
                        OR
                    </p>
                </div>

                <div
                    style={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        borderBottom: '1px solid #4a6c72',
                        opacity: !!listFiles.length || isLoading ? '0.5' : '1',
                    }}
                >
                    <input
                        type={'text'}
                        placeholder="Image URL"
                        value={fileUrl}
                        disabled={!!listFiles.length || isLoading}
                        className={classes.input}
                        onChange={(e) => {
                            setFileUrl(e.target.value);
                            if (invalidFileUrl) {
                                setInvalidFileUrl(false);
                            }
                        }}
                    />
                    {fileUrl && isLoading ? (
                        <div className="position-absolute end-0">
                            <CircularProgress size={15} className={classes.loadingIcon} />
                        </div>
                    ) : null}
                </div>
                {fileUrl && !ValidURL(fileUrl) ? (
                    <span className={`${classes.errorMessage} align-self-start`}>Invalid url</span>
                ) : invalidFileUrl ? (
                    <span className={`${classes.errorMessage} align-self-start`}>Invalid image url</span>
                ) : null}
            </DialogContent>

            <div
                style={{
                    width: '100%',
                    // border: '1px solid #E8F0F1',
                    marginTop: '30px',
                }}
            />

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    margin: '20px 0px',
                }}
            >
                <Button
                    variant="outlined"
                    className={classes.btn}
                    onClick={() => {
                        handleClearForm();
                        setIsOpen(false);
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={!isButtonSaveEnable || isLoading}
                    onClick={handleSaveBtn}
                    className={clsx(classes.btn, classes.activeBtn)}
                    style={{
                        opacity: isButtonSaveEnable ? '1' : '0.5',
                        marginLeft: '10px',
                    }}
                >
                    Save
                </Button>
            </div>
            {modalShow.show && (
                <StandardPopup
                    show={modalShow.show}
                    onHide={() => {
                        setModalShow({ ...modalShow, show: false });
                        console.log('on hiding');
                    }}
                    title={modalShow.title}
                    content={modalShow.content}
                    button1={modalShow.button1}
                    button2={modalShow.button2}
                    type={modalShow.type as any}
                    closeButton={modalShow.closeButton}
                    onSecondButtonClick={() => {
                        if (modalShow.path) history.push(modalShow.path);
                    }}
                />
            )}
        </Dialog>
    );
};

export default ModalAddImage;
